import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { v4API } from '../../Api';
import { actionKey, dateTime, errorMessage } from './common';
import { moneyToUnits, unitsToMoney } from './configurationFields';
import { useV4 } from './V4Layout';

const storageKey = 'v4-site-balance-adjustment';
const emptyPage = { items: [], total: 0 };
const pageData = data => ({ items: Array.isArray(data?.items) ? data.items : [],
  total: Number.isSafeInteger(data?.total) && data.total >= 0 ? data.total : 0 });
const rub = value => typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value) ? `${value} RUB` : '—';
const scopeKey = wallet => JSON.stringify([wallet.merchantId, wallet.login]);
function storedRequest() {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const request = JSON.parse(raw);
    if (typeof request?.merchantId !== 'string' || typeof request.login !== 'string' || typeof request.key !== 'string' ||
        typeof request.body?.amount !== 'string' || typeof request.body.expectedBalance !== 'string' ||
        typeof request.body.reason !== 'string') return { unreadable: true };
    return request;
  } catch { return { unreadable: true }; }
}
export function siteAdjustment(balance, amount) {
  try {
    const raw = amount.trim().replace(',', '.');
    if (!/^-?(0|[1-9]\d*)(?:\.\d{1,2})?$/.test(raw)) return null;
    const absolute = moneyToUnits(raw.replace(/^-/, ''), 2), units = window.BigInt(absolute);
    if (!units) return null;
    const after = window.BigInt(moneyToUnits(balance, 2)) + (raw.startsWith('-') ? -units : units);
    if (after < 0n) return null;
    return { amount: `${raw.startsWith('-') ? '-' : ''}${unitsToMoney(absolute, 2)}`, after: unitsToMoney(after.toString(), 2) };
  } catch { return null; }
}
function Pagination({ result, page, setPage, disabled }) {
  return <TablePagination component="div" count={result.total} page={page - 1} rowsPerPage={25} rowsPerPageOptions={[25]}
    labelRowsPerPage="На странице" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
    onPageChange={(_, next) => setPage(next + 1)} disabled={disabled} />;
}

function WalletDialog({ selected, pendingRequest, setPendingRequest, onClose, onChanged }) {
  const { merchantId, login } = selected;
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState(emptyPage);
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ amount: pendingRequest?.body?.amount || '', reason: pendingRequest?.body?.reason || '' });
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false), mounted = useRef(true);
  const pending = pendingRequest?.unreadable || (pendingRequest && scopeKey(pendingRequest) === scopeKey(selected));
  const prepared = wallet ? siteAdjustment(wallet.balance, form.amount) : null;
  const valid = prepared && form.reason.trim() && form.reason.trim().length <= 1024;
  const locked = busy || !!pending;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    Promise.all([v4API.siteBalance(merchantId, login, { signal: controller.signal }),
      v4API.siteBalanceOperations(merchantId, login, { page, limit: 25 }, { signal: controller.signal })])
      .then(([current, journal]) => {
        if (!controller.signal.aborted) { setWallet(current); setHistory(pageData(journal)); setReview(false); }
      })
      .catch(cause => { if (!controller.signal.aborted) { setWallet(null); setError(errorMessage(cause)); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [merchantId, login, page, reload]);

  async function submit() {
    if (sending.current || pendingRequest?.unreadable || (!pending && (!valid || !review))) return;
    const request = pending ? pendingRequest : { merchantId, login, key: actionKey(),
      body: { amount: prepared.amount, expectedBalance: wallet.balance, reason: form.reason.trim() } };
    if (scopeKey(request) !== scopeKey(selected)) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify(request)); }
    catch { setError('Не удалось сохранить ключ повтора. Корректировка не отправлена.'); return; }
    sending.current = true;
    setBusy(true); setError(''); setNotice(''); setPendingRequest(request);
    try {
      await v4API.adjustSiteBalance(request.merchantId, request.login, request.body, request.key);
      sessionStorage.removeItem(storageKey);
      setPendingRequest(null);
      onChanged();
      if (mounted.current) {
        setNotice('Корректировка записана.'); setForm({ amount: '', reason: '' }); setReview(false);
        setReload(value => value + 1);
      }
    } catch (cause) {
      const code = cause?.response?.data?.error?.code;
      if (['SITE_BALANCE_STALE', 'SITE_INSUFFICIENT_BALANCE', 'INVALID_AMOUNT', 'INVALID_FIELDS',
        'INVALID_REASON', 'INVALID_LOGIN', 'MERCHANT_NOT_FOUND'].includes(code)) {
        sessionStorage.removeItem(storageKey);
        setPendingRequest(null);
        if (mounted.current) { setReview(false); setReload(value => value + 1); }
      }
      if (mounted.current) setError(errorMessage(cause));
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  function change(field, value) {
    setForm(current => ({ ...current, [field]: value })); setReview(false); setNotice('');
  }
  return <Dialog open onClose={() => { if (!locked) onClose(); }} disableEscapeKeyDown={locked} fullWidth maxWidth="md">
    <DialogTitle>Баланс сайта: {login}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Typography>Мерчант: {selected.merchantName || merchantId}</Typography>
      {selected.merchantName && <Typography variant="caption">ID: {merchantId}</Typography>}
      {(loading || busy) && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      {wallet && <Typography variant="h6">Баланс: {rub(wallet.balance)}</Typography>}
      {pending && <Alert severity="warning">Результат предыдущей корректировки нужно подтвердить. Повтор использует прежний ключ и ту же сумму.</Alert>}
      <TextField label="Изменение баланса (RUB)" value={form.amount} disabled={locked}
        helperText="Положительная сумма — пополнение, отрицательная — уменьшение."
        onChange={event => change('amount', event.target.value)} />
      <TextField label="Причина корректировки" value={form.reason} disabled={locked} multiline
        inputProps={{ maxLength: 1024 }} onChange={event => change('reason', event.target.value)} />
      {review && prepared && <Alert severity="info">После корректировки: {rub(wallet.balance)} → {rub(prepared.after)}. Мерчант: {merchantId}, логин: {login}.</Alert>}
      {pending ? <Button variant="contained" disabled={busy || pendingRequest?.unreadable} onClick={submit}>Повторить ту же корректировку</Button>
        : review ? <Button variant="contained" disabled={loading || busy || !valid} onClick={submit}>Подтвердить корректировку</Button>
          : <Button disabled={loading || !valid} onClick={() => setReview(true)}>Проверить корректировку</Button>}
      <Typography variant="h6">История операций</Typography>
      <TableContainer><Table size="small"><TableHead><TableRow>
        <TableCell>Дата</TableCell><TableCell>Операция</TableCell><TableCell>Сумма</TableCell>
        <TableCell>Остаток после</TableCell><TableCell>Основание</TableCell>
      </TableRow></TableHead><TableBody>
        {history.items.map(row => <TableRow key={row.operationId}>
          <TableCell>{dateTime(row.createdAt)}</TableCell>
          <TableCell>{{ deposit: 'Пополнение', purchase: 'Покупка', adjustment: 'Корректировка' }[row.type] || 'Операция'}</TableCell>
          <TableCell>{rub(row.amount)}</TableCell><TableCell>{rub(row.balanceAfter)}</TableCell>
          <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.reason || '—'}
            {row.orderId && <><br /><Link to={`/v4/orders/${encodeURIComponent(row.orderId)}`}>Ордер {row.orderId}</Link></>}
          </TableCell>
        </TableRow>)}
        {!loading && !history.items.length && <TableRow><TableCell colSpan={5}>Операций нет</TableCell></TableRow>}
      </TableBody></Table></TableContainer>
      <Pagination result={history} page={page} setPage={setPage} disabled={loading || busy} />
    </Stack></DialogContent>
    <DialogActions><Button disabled={loading || busy} onClick={() => setReload(value => value + 1)}>Обновить карточку</Button>
      <Button disabled={locked} onClick={onClose}>Закрыть</Button></DialogActions>
  </Dialog>;
}

export default function SiteBalances() {
  const { system } = useV4(), ready = system?.ready === true;
  const [pendingRequest, setPendingRequest] = useState(storedRequest);
  const [selected, setSelected] = useState(() => {
    const saved = storedRequest();
    return saved && !saved.unreadable ? { merchantId: saved.merchantId, login: saved.login } : null;
  });
  const [form, setForm] = useState({ merchantId: '', login: '' });
  const [filters, setFilters] = useState({});
  const [result, setResult] = useState(emptyPage);
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!ready) return undefined;
    const controller = new AbortController();
    setLoading(true); setError('');
    v4API.siteBalances({ ...filters, page, limit: 25 }, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) setResult(pageData(data)); })
      .catch(cause => { if (!controller.signal.aborted) { setResult(emptyPage); setError(errorMessage(cause)); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [ready, filters, page, reload]);
  function search(event) {
    event.preventDefault();
    setFilters({ ...(form.merchantId.trim() && { merchantId: form.merchantId.trim() }),
      ...(form.login.trim() && { login: form.login.trim() }) });
    setPage(1);
  }
  return <Stack spacing={2}>
    <Typography variant="h5" component="h2">Балансы сайтов</Typography>
    <Typography>Каждый логин имеет отдельный баланс у каждого мерчанта. Все остатки — в RUB.</Typography>
    {!ready && <Alert severity="info">Сначала подготовьте V4 в разделе <Link to="/v4/system">«Система»</Link>.</Alert>}
    {pendingRequest?.unreadable && <Alert severity="error">Не удалось прочитать сохранённую корректировку. Проверьте историю и восстановите запись повтора перед новой операцией.</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    <Stack component="form" onSubmit={search} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <TextField label="ID мерчанта" value={form.merchantId} onChange={event => setForm({ ...form, merchantId: event.target.value })} />
      <TextField label="Логин" value={form.login} helperText="Точное совпадение, с учётом регистра."
        onChange={event => setForm({ ...form, login: event.target.value })} />
      <Button type="submit" disabled={!ready || loading}>Найти</Button>
      <Button disabled={!ready || !form.merchantId.trim() || !form.login.trim() || !!pendingRequest}
        onClick={() => setSelected({ merchantId: form.merchantId.trim(), login: form.login.trim() })}>Открыть счёт</Button>
      <Button disabled={!ready || loading} onClick={() => setReload(value => value + 1)}>Обновить</Button>
    </Stack>
    {loading && <LinearProgress />}
    <TableContainer component={Paper}><Table><TableHead><TableRow>
      <TableCell>Мерчант</TableCell><TableCell>Логин</TableCell><TableCell>Баланс</TableCell>
      <TableCell>Обновлён</TableCell><TableCell>Действия</TableCell>
    </TableRow></TableHead><TableBody>
      {result.items.map(row => <TableRow key={scopeKey(row)}>
        <TableCell>{row.merchantName || row.merchantId}{row.merchantName && <Typography variant="caption" display="block">{row.merchantId}</Typography>}</TableCell>
        <TableCell>{row.login}</TableCell><TableCell>{rub(row.balance)}</TableCell><TableCell>{dateTime(row.updatedAt)}</TableCell>
        <TableCell><Button disabled={!!pendingRequest} onClick={() => setSelected(row)}>История и баланс</Button></TableCell>
      </TableRow>)}
      {!loading && !result.items.length && <TableRow><TableCell colSpan={5}>Счетов по выбранным условиям нет</TableCell></TableRow>}
    </TableBody></Table></TableContainer>
    <Pagination result={result} page={page} setPage={setPage} disabled={!ready || loading} />
    {ready && selected && <WalletDialog key={scopeKey(selected)} selected={selected} pendingRequest={pendingRequest}
      setPendingRequest={setPendingRequest} onClose={() => setSelected(null)} onChanged={() => setReload(value => value + 1)} />}
  </Stack>;
}
